// The chain as a tree of subsystems, as the tree view draws it. Each kind of
// part stated directly in the root starts a branch, and the branches are
// grouped into subsystems by the curated area of the kind that starts them:
// navigation, propulsion, sensing. Within a subsystem a kind hangs under the
// host that states it most often, its home. A kind stated inside kinds of
// several subsystems appears in each, its own parts listed once, at home;
// another host in its own subsystem is a cross link. Pure: it knows nothing
// of React or of the canvas.
//
// A platform the root carries (a guided missile under an aircraft) is drawn
// closed: what is stated inside it is a chain of its own, and the kinds
// reached only through it are left out. A card of rank l1 names no kind of
// thing: it is left out as the tier views leave it, and a kind stated inside
// it counts as stated in the root.
//
// The view shows one subsystem at a time, and of it at most `TREE_LEVELS`
// levels: the trail of open kinds leads down, and the levels above the
// window are drawn small, as a path.
//
// The same tree is grown from the other side for what the root is built
// into (the integration): each kind the root is stated in starts a branch,
// a kind hangs under the part it is most often stated to hold, and the
// branches are grouped by the curated area of their first host: the domains
// of the platforms (air, maritime), the areas of the equipment and modules.
// No host is drawn closed: a platform carried by another is followed.

import { ChainCard, ChainEdge, ChainSide } from "../../types/valueChain";
import {
  COLUMN_CARD_LIMIT,
  CollapsedRow,
  DrawnChain,
  cardName,
} from "./chainModel";
import { BRANCH_RANK, NO_AREA_NAME, isBroad } from "./tierModel";

// Levels of the open subsystem the window shows
export const TREE_LEVELS = 3;

// Subsystems the tree names before the smallest are folded into one
export const SUBSYSTEM_LIMIT = 12;

// The kinds of a subsystem small enough to be folded, when there are more
// than the limit
export const SMALL_SUBSYSTEM = 2;

// Where the small subsystems go
export const OTHER_SUBSYSTEM = "Other";

// The subsystem of a branch by the nature of the kind that starts it, before
// its area: what the root carries, its loose parts, its materials
export const CARRIED_SUBSYSTEM = "Carried platforms";
const NATURE_SUBSYSTEMS: Readonly<Record<string, string>> = {
  platform: CARRIED_SUBSYSTEM,
  part: "Parts",
  material: "Materials",
};

// The nature of a kind the root carries: drawn closed
const CARRIED_NATURE = "platform";

// The areas of the infrastructure branch that name the same subsystem as an
// area of the modules
const AREA_ALIASES: Readonly<Record<string, string>> = {
  Communications: "Communications & networking",
  "Navigation & timing": "Navigation, positioning & timing",
};

// One kind as the tree places it in one subsystem
export interface TreePlacement {
  card: ChainCard;
  subsystem: string;
  // The kind it hangs under (in the tree of hosts, the part it holds); null
  // for a kind of the subsystem's first level
  host: string | null;
  // The stated edge from that kind, from the root for the first level
  edge: ChainEdge;
  // At home in another subsystem, where its parts are listed
  shared: boolean;
}

export interface Subsystem {
  name: string;
  // Its kinds at home
  kinds: number;
  // The kinds at home in another subsystem that it uses too
  shared: number;
  // The stated pairs from the root into its first level
  statements: number;
}

export interface ChainTree {
  root: ChainCard;
  // The side of the root it is grown from: its parts, or what it is built
  // into
  side: ChainSide;
  // The most statements from the root first; the folded and the carried
  // ones last
  subsystems: Subsystem[];
  // The kinds under each host of each subsystem, by `childrenKey`, the
  // strongest edge first
  children: ReadonlyMap<string, TreePlacement[]>;
  // Each kind at home
  homes: ReadonlyMap<string, TreePlacement>;
  // The subsystems each kind appears in, its home first
  subsystemsOf: ReadonlyMap<string, string[]>;
  // A kind in a subsystem it is shared into, by `placementKey`
  sharedPlacements: ReadonlyMap<string, TreePlacement>;
  // The other hosts of a placed kind in the same subsystem, by
  // `placementKey`
  crossEdges: ReadonlyMap<string, ChainEdge[]>;
  // Kinds reached only through a carried platform: left out
  insideCarried: number;
}

export const childrenKey = (subsystem: string, host: string | null): string =>
  `${subsystem}|${host ?? ""}`;

export const placementKey = (subsystem: string, card: string): string =>
  `${subsystem}|${card}`;

// Whether a kind is one the root carries
export const isCarried = (card: ChainCard): boolean =>
  card.side === "parts" && card.nature === CARRIED_NATURE;

// The end of an edge a tree grown from a side hangs the other on: the host
// of the parts, the part of the hosts
export const treeParentOf = (side: ChainSide, edge: ChainEdge): string =>
  side === "parts" ? edge.host : edge.part;

// The end of an edge that hangs in a tree grown from a side
export const treeChildOf = (side: ChainSide, edge: ChainEdge): string =>
  side === "parts" ? edge.part : edge.host;

// The subsystem a branch belongs to, by the kind that starts it: by its
// nature, then its area, on the parts side; by its area alone on the hosts
// side, where the area of a platform names its domain
const subsystemOf = (card: ChainCard, side: ChainSide): string => {
  const byNature = side === "parts" ? NATURE_SUBSYSTEMS[card.nature ?? ""] : undefined;
  if (byNature) {
    return byNature;
  }
  if (!card.area) {
    return NO_AREA_NAME;
  }
  const name = card.area.name ?? card.area.id;
  return AREA_ALIASES[name] ?? name;
};

// The order of the edges a kind hangs by: the most stated pairs first, then
// the most typical rows, the root before a kind, then by name
const strongestFirst =
  (names: ReadonlyMap<string, string>, rootId: string, parentOf: (edge: ChainEdge) => string) =>
  (a: ChainEdge, b: ChainEdge): number =>
    b.pairs - a.pairs ||
    b.typical - a.typical ||
    Number(parentOf(b) === rootId) - Number(parentOf(a) === rootId) ||
    (names.get(parentOf(a)) ?? parentOf(a)).localeCompare(
      names.get(parentOf(b)) ?? parentOf(b),
    );

// The order of the kinds under one host: the broad ones last, then by the
// edge from the host, then by products and name
const byEdge = (a: TreePlacement, b: TreePlacement): number =>
  Number(isBroad(a.card)) - Number(isBroad(b.card)) ||
  b.edge.pairs - a.edge.pairs ||
  b.edge.typical - a.edge.typical ||
  b.card.product_count - a.card.product_count ||
  cardName(a.card).localeCompare(cardName(b.card));

// One side of a drawn chain as a tree of subsystems: its parts, or what it
// is built into
export const buildTree = (
  drawn: DrawnChain,
  side: ChainSide = "parts",
): ChainTree => {
  const rootId = drawn.root.id;
  const parentOf = (edge: ChainEdge): string => treeParentOf(side, edge);
  const childOf = (edge: ChainEdge): string => treeChildOf(side, edge);
  const all = Array.from(drawn.cards.values()).filter(
    (card) => card.side === side,
  );
  // The cards of rank l1 stand in for the root
  const rootLike = new Set([
    rootId,
    ...all.filter((card) => card.rank === BRANCH_RANK).map((card) => card.id),
  ]);
  const cards = new Map(
    all
      .filter((card) => card.rank !== BRANCH_RANK)
      .map((card): [string, ChainCard] => [card.id, card]),
  );
  const names = new Map(
    [drawn.root, ...all].map((card): [string, string] => [card.id, cardName(card)]),
  );
  const fromRoot = (edge: ChainEdge): boolean => rootLike.has(parentOf(edge));

  // What is stated inside a carried platform is not followed
  const edges = drawn.edges.filter((edge) => {
    if (!cards.has(childOf(edge))) {
      return false;
    }
    if (fromRoot(edge)) {
      return true;
    }
    const parent = cards.get(parentOf(edge));
    return parent !== undefined && !isCarried(parent);
  });

  // The kinds reached from the root
  const outOf = new Map<string, string[]>();
  for (const edge of edges) {
    const from = fromRoot(edge) ? rootId : parentOf(edge);
    outOf.set(from, [...(outOf.get(from) ?? []), childOf(edge)]);
  }
  const reached = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    for (const part of outOf.get(queue.shift()!) ?? []) {
      if (!reached.has(part)) {
        reached.add(part);
        queue.push(part);
      }
    }
  }

  // The edges each kind hangs by, the strongest first; a card of rank l1 as
  // the root
  const order = strongestFirst(names, rootId, (edge) =>
    fromRoot(edge) ? rootId : parentOf(edge),
  );
  const hostsOf = new Map<string, ChainEdge[]>();
  for (const edge of edges) {
    const child = childOf(edge);
    if (reached.has(child) && (fromRoot(edge) || reached.has(parentOf(edge)))) {
      hostsOf.set(child, [...(hostsOf.get(child) ?? []), edge]);
    }
  }
  hostsOf.forEach((list) => list.sort(order));

  // Each kind's home host, null for the root. When the homes lead round in a
  // circle, the kind of it the root states most often hangs on the root.
  const homeHost = new Map<string, string | null>();
  reached.forEach((id) => {
    const strongest = hostsOf.get(id)![0];
    homeHost.set(id, fromRoot(strongest) ? null : parentOf(strongest));
  });
  const fromRootPairs = (id: string): number =>
    Math.max(
      -1,
      ...(hostsOf.get(id) ?? []).filter(fromRoot).map((edge) => edge.pairs),
    );
  const headOf = new Map<string, string>();
  const findHead = (id: string): string => {
    const walked: string[] = [];
    let at = id;
    for (;;) {
      const known = headOf.get(at);
      if (known) {
        walked.forEach((seen) => headOf.set(seen, known));
        return known;
      }
      walked.push(at);
      const up = homeHost.get(at) ?? null;
      if (up === null) {
        const head = at;
        walked.forEach((seen) => headOf.set(seen, head));
        return head;
      }
      if (walked.includes(up)) {
        const circle = walked.slice(walked.indexOf(up));
        const head = circle.reduce((best, candidate) =>
          fromRootPairs(candidate) > fromRootPairs(best) ? candidate : best,
        );
        homeHost.set(head, null);
        walked.forEach((seen) => headOf.set(seen, head));
        return head;
      }
      at = up;
    }
  };

  // Each kind's subsystem: that of its branch, the smallest folded when
  // there are too many
  const branchOf = new Map<string, string>();
  reached.forEach((id) =>
    branchOf.set(id, subsystemOf(cards.get(findHead(id))!, side)),
  );
  const sizes = new Map<string, number>();
  branchOf.forEach((name) => sizes.set(name, (sizes.get(name) ?? 0) + 1));
  const fold = sizes.size > SUBSYSTEM_LIMIT;
  const homeOf = new Map<string, string>();
  branchOf.forEach((name, id) =>
    homeOf.set(
      id,
      fold && (sizes.get(name) ?? 0) <= SMALL_SUBSYSTEM ? OTHER_SUBSYSTEM : name,
    ),
  );

  // Every kind at home, and in each other subsystem a kind of which states
  // it, under the strongest such host
  const homes = new Map<string, TreePlacement>();
  const sharedPlacements = new Map<string, TreePlacement>();
  const subsystemsOf = new Map<string, string[]>();
  const crossEdges = new Map<string, ChainEdge[]>();
  const children = new Map<string, TreePlacement[]>();
  const place = (placement: TreePlacement) => {
    const key = childrenKey(placement.subsystem, placement.host);
    children.set(key, [...(children.get(key) ?? []), placement]);
  };
  reached.forEach((id) => {
    const card = cards.get(id)!;
    const hosts = hostsOf.get(id)!;
    const subsystem = homeOf.get(id)!;
    const host = homeHost.get(id) ?? null;
    const home: TreePlacement = {
      card,
      subsystem,
      host,
      edge:
        (host === null
          ? hosts.find(fromRoot)
          : hosts.find((edge) => parentOf(edge) === host)) ?? hosts[0],
      shared: false,
    };
    homes.set(id, home);
    place(home);
    const placed = [home];
    for (const edge of hosts) {
      const other = fromRoot(edge) ? null : homeOf.get(parentOf(edge));
      if (other && !placed.some((at) => at.subsystem === other)) {
        const shared: TreePlacement = {
          card,
          subsystem: other,
          host: parentOf(edge),
          edge,
          shared: true,
        };
        sharedPlacements.set(placementKey(other, id), shared);
        placed.push(shared);
        place(shared);
      }
    }
    subsystemsOf.set(
      id,
      placed.map((at) => at.subsystem),
    );
    for (const at of placed) {
      const others = hosts.filter(
        (edge) =>
          !fromRoot(edge) &&
          parentOf(edge) !== at.host &&
          homeOf.get(parentOf(edge)) === at.subsystem,
      );
      if (others.length > 0) {
        crossEdges.set(placementKey(at.subsystem, id), others);
      }
    }
  });
  children.forEach((list) => list.sort(byEdge));

  const subsystems = Array.from(new Set(homeOf.values()))
    .map((name): Subsystem => {
      const first = children.get(childrenKey(name, null)) ?? [];
      return {
        name,
        kinds: Array.from(homes.values()).filter((at) => at.subsystem === name)
          .length,
        shared: Array.from(sharedPlacements.values()).filter(
          (at) => at.subsystem === name,
        ).length,
        statements: first.reduce((total, at) => total + at.edge.pairs, 0),
      };
    })
    .sort(
      (a, b) =>
        Number(a.name === CARRIED_SUBSYSTEM || a.name === OTHER_SUBSYSTEM) -
          Number(b.name === CARRIED_SUBSYSTEM || b.name === OTHER_SUBSYSTEM) ||
        b.statements - a.statements ||
        b.kinds - a.kinds ||
        a.name.localeCompare(b.name),
    );

  return {
    root: drawn.root,
    side,
    subsystems,
    children,
    homes,
    subsystemsOf,
    sharedPlacements,
    crossEdges,
    insideCarried: cards.size - reached.size,
  };
};

// The trail that shows a kind in a subsystem: the kinds above it, from the
// subsystem's first level down. Empty for a kind not in the subsystem.
export const trailTo = (
  tree: ChainTree,
  card: string,
  subsystem: string,
): string[] => {
  const home = tree.homes.get(card);
  const at =
    home?.subsystem === subsystem
      ? home
      : tree.sharedPlacements.get(placementKey(subsystem, card));
  const trail: string[] = [];
  let host = at?.host ?? null;
  while (host !== null && !trail.includes(host)) {
    trail.unshift(host);
    host = tree.homes.get(host)?.host ?? null;
  }
  return trail;
};

// What a card of the tree view knows of its place
export interface TreeCardInfo {
  placement: TreePlacement;
  // The side the tree is grown from: under a card hang its parts, or its
  // hosts
  side: ChainSide;
  // The kinds under it in its subsystem; none for a shared kind
  parts: number;
  // Open on the trail
  open: boolean;
  // Drawn small, on the path above the window
  spine: boolean;
  // The trail it sets when pressed: its parts opened or closed, or, on the
  // path, the window moved back to it
  toggle: string[];
  // For a kind at home here, the other subsystems it appears in; for a
  // shared kind, its home
  elsewhere: string[];
}

// The tree view's state: the open subsystem and the kinds open in it
export interface TreeState {
  branch: string | null;
  trail: readonly string[];
}

// One level of the window: the kinds under one host, the first ones and the
// open one, then a row naming the rest
export interface TreeColumn {
  // Its level in the subsystem: 0 for the kinds stated in the root
  level: number;
  host: string | null;
  // The kind of it open on the trail; null for the last level
  open: string | null;
  placements: TreePlacement[];
  collapsed: CollapsedRow | null;
}

export interface VisibleTree {
  tree: ChainTree;
  // The open subsystem; null when every one is closed
  branch: Subsystem | null;
  // The trail as far as it leads down the subsystem
  trail: string[];
  // The open kinds above the window, drawn small
  spine: TreePlacement[];
  // The levels in the window
  columns: TreeColumn[];
}

// The id of the row of a level, by the subsystem and the host
export const treeRowId = (subsystem: string, host: string | null): string =>
  `tree:${childrenKey(subsystem, host)}`;

// What the tree view shows: the open subsystem and, of it, the last `levels`
// levels the trail opens. A level of more than `limit` kinds shows the first
// ones and the open one, and a row naming the rest, unless that row is shown
// whole (by its id). A trail that leaves the subsystem is cut there; a kind
// open on it whose parts are not read yet opens no level.
export const visibleTree = (
  tree: ChainTree,
  state: TreeState,
  shownWhole: ReadonlySet<string>,
  levels: number = TREE_LEVELS,
  limit: number = COLUMN_CARD_LIMIT,
): VisibleTree => {
  const branch =
    tree.subsystems.find((subsystem) => subsystem.name === state.branch) ?? null;
  if (!branch) {
    return { tree, branch: null, trail: [], spine: [], columns: [] };
  }
  const kindsUnder = (host: string | null): TreePlacement[] =>
    tree.children.get(childrenKey(branch.name, host)) ?? [];

  const trail: string[] = [];
  const opened: TreePlacement[] = [];
  let host: string | null = null;
  for (const id of state.trail) {
    const at = kindsUnder(host).find(
      (placement) => placement.card.id === id && !placement.shared,
    );
    if (!at) {
      break;
    }
    trail.push(id);
    opened.push(at);
    host = id;
  }

  const hosts: (string | null)[] = [null, ...trail];
  const count = kindsUnder(hosts[hosts.length - 1]).length > 0
    ? hosts.length
    : hosts.length - 1;
  const first = Math.max(0, count - levels);
  const columns: TreeColumn[] = [];
  for (let level = first; level < count; level++) {
    const at = hosts[level];
    const open = trail[level] ?? null;
    const all = kindsUnder(at);
    const id = treeRowId(branch.name, at);
    if (all.length <= limit || shownWhole.has(id)) {
      columns.push({ level, host: at, open, placements: all, collapsed: null });
      continue;
    }
    const shown = all.filter(
      (placement, index) => index < limit || placement.card.id === open,
    );
    const hostCard = at === null ? null : tree.homes.get(at)?.card;
    columns.push({
      level,
      host: at,
      open,
      placements: shown,
      collapsed: {
        id,
        step: level + 1,
        side: tree.side,
        cards: all
          .filter((placement) => !shown.includes(placement))
          .map((placement) => placement.card),
        stub: false,
        area: branch.name,
        areaName: hostCard ? cardName(hostCard) : branch.name,
      },
    });
  }
  return { tree, branch, trail, spine: opened.slice(0, first), columns };
};
